import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { BpmnModelerService } from '../services/bpmn-modeler.service';
import {
  BpmnLintResult,
  BpmnLintIssue,
  BpmnLintService,
} from '../services/bpmn-lint.service';
import { ProcessDefineControllerService } from '../../../../api/order-service/api/processDefineController.service';
import { ProcessDefineDTO } from '../../../../api/order-service/model/processDefineDTO';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { TextNormalizerService } from '@shared/services/text-normalizer.service';
import { vietnameseTextPatternValidator } from '../../../center-business/goods/classify/models/classify.config';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-bpmn-create',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    TranslateModule,
    ReactiveFormsModule,
    AppButtonComponent,
  ],
  templateUrl: './bpmn-create.component.html',
  styleUrl: './bpmn-create.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpmnCreateComponent
  implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate
{
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly bpmnModelerService = inject(BpmnModelerService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly processDefineService = inject(
    ProcessDefineControllerService
  );
  private readonly destroy$ = new Subject<void>();

  private readonly bpmnLintService = inject(BpmnLintService);
  private readonly textNormalizer = inject(TextNormalizerService);
  private skipCanDeactivate = false;

  // Reactive form for process information
  processForm!: FormGroup;

  @ViewChild('modelerContainer', { static: false })
  modelerContainer!: ElementRef;

  @ViewChild('propertiesPanelContainer', { static: false })
  propertiesPanelContainer!: ElementRef;

  // Signals for component state
  loading = signal(false);
  hasUnsavedChanges = signal(false);
  lintResults = signal<BpmnLintResult>({
    issues: [],
    hasErrors: false,
    hasWarnings: false,
    hasInfo: false,
  });

  // Computed properties
  isSaveDisabled = computed(() => {
    return this.loading();
  });

  hasLintErrors = computed(() => {
    return this.lintResults().hasErrors;
  });

  hasLintWarnings = computed(() => {
    return this.lintResults().hasWarnings;
  });

  hasLintIssues = computed(() => {
    const results = this.lintResults();
    return results.hasErrors || results.hasWarnings || results.hasInfo;
  });

  lintErrorCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'error'
    ).length;
  });

  lintWarningCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'warn'
    ).length;
  });

  lintInfoCount = computed(() => {
    return this.lintResults().issues.filter(
      (issue) => issue.severity === 'info'
    ).length;
  });

  // Note: Linting overlay detection disabled due to container access issues
  // lintOverlayVisible = computed(() => {
  //   return this.bpmnModelerService.lintOverlayVisible();
  // });

  ngOnInit(): void {
    // Initialize reactive form
    this.processForm = this.fb.group({
      processName: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(100),
        ],
      ],
      processDefineId: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          vietnameseTextPatternValidator(/^[a-zA-Z0-9_-]+$/, this.textNormalizer),
        ],
      ],
    });

    // Track form changes for unsaved changes detection
    this.processForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    // Initialize BPMN modeler after view is ready
    this.initializeBpmnModeler();
    this.setupBpmnChangeListener();
    this.setupLintingListener();
  }

  /**
   * Get form control for easier access in template
   */
  getFormControl(controlName: string) {
    return this.processForm.get(controlName);
  }

  /**
   * Check if a form field has an error
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.getFormControl(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a form field
   */
  getFieldErrorMessage(fieldName: string): string {
    const control = this.getFormControl(fieldName);
    if (!control || !control.errors) return '';

    // Map field names to translation key format
    const fieldKeyMap: { [key: string]: string } = {
      'processName': 'process_name',
      'processDefineId': 'process_define_id'
    };
    const fieldKey = fieldKeyMap[fieldName] || fieldName;

    if (control.errors['required']) {
      return this.translateService.instant(`bpmn.create.form.validation.${fieldKey}_required`);
    }
    if (control.errors['minlength']) {
      return this.translateService.instant(`bpmn.create.form.validation.${fieldKey}_min_length`);
    }
    if (control.errors['maxlength']) {
      return this.translateService.instant('common.validation.maxlength', {
        fieldName: this.translateService.instant(`bpmn.create.form.${fieldKey}`),
        requiredLength: control.errors['maxlength'].requiredLength,
      });
    }
    if (control.errors['pattern']) {
      return this.translateService.instant('common.validation.pattern');
    }

    return '';
  }

  /**
   * Check if BPMN modeler has content (more than just an empty process)
   */
  private hasBpmnContent(): boolean {
    try {
      const modeler = this.bpmnModelerService.getModeler();
      if (!modeler) return false;

      const elementRegistry = modeler.get('elementRegistry') as any;
      const elements = elementRegistry.getAll();

      // Check if there are any meaningful BPMN elements
      const meaningfulElements = elements.filter((element: any) => {
        const type = element.type;
        // Exclude structural elements and include only process elements
        return (
          type &&
          !type.includes('bpmn:Process') &&
          !type.includes('bpmn:Collaboration') &&
          !type.includes('bpmn:Participant') &&
          !type.includes('bpmn:BPMNDiagram') &&
          !type.includes('bpmn:BPMNPlane') &&
          !type.includes('bpmn:BPMNShape') &&
          !type.includes('bpmn:BPMNEdge')
        );
      });

      return meaningfulElements.length > 0;
    } catch (error) {
      console.warn('Error checking BPMN content:', error);
      return false;
    }
  }

  /**
   * Initialize BPMN modeler
   */
  private async initializeBpmnModeler(): Promise<void> {
    try {
      if (
        this.modelerContainer?.nativeElement &&
        this.propertiesPanelContainer?.nativeElement
      ) {
        await this.bpmnModelerService.initialize({
          container: this.modelerContainer.nativeElement,
          propertiesPanel: this.propertiesPanelContainer.nativeElement,
          width: '100%',
          height: '100%',
        });
      }
    } catch (error) {
      console.error('Failed to initialize BPMN modeler:', error);
    }
  }

  /**
   * Setup listener for BPMN linting results
   */
  private setupLintingListener(): void {
    this.bpmnModelerService
      .getLintResults$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((results: BpmnLintResult) => {
        this.lintResults.set(results);
        this.cdr.markForCheck();

        // Log linting results for debugging
        if (results.issues.length > 0) {
        }
      });
  }

  /**
   * Setup listener for BPMN changes to update save button state
   */
  private setupBpmnChangeListener(): void {
    try {
      const modeler = this.bpmnModelerService.getModeler();
      if (!modeler) return;

      // Listen for element changes
      const eventBus = modeler.get('eventBus') as any;
      const commandStack = modeler.get('commandStack') as any;

      // Use command stack changes for more reliable change detection
      if (commandStack && typeof commandStack.on === 'function') {
        commandStack.on('changed', () => {
          this.hasUnsavedChanges.set(true);
          this.cdr.markForCheck();
        });
      }

      // Also listen to specific element events as backup
      eventBus.on('element.changed', () => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });

      eventBus.on('element.added', () => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });

      eventBus.on('element.removed', () => {
        this.hasUnsavedChanges.set(true);
        this.cdr.markForCheck();
      });
    } catch (error) {
      console.warn('Failed to setup BPMN change listener:', error);
    }
  }

  /**
   * Clear all BPMN content
   */
  async onClearAll(): Promise<void> {
    try {
      // Clear BPMN modeler - this will reset to empty process
      await this.bpmnModelerService.clear();
    } catch (error) {
      console.error('Failed to clear BPMN modeler:', error);
    }
  }

  /**
   * Zoom in the BPMN canvas
   */
  onZoomIn(): void {
    this.bpmnModelerService.zoomIn();
  }

  /**
   * Zoom out the BPMN canvas
   */
  onZoomOut(): void {
    this.bpmnModelerService.zoomOut();
  }

  /**
   * Fit the BPMN diagram to viewport
   */
  onFitToViewport(): void {
    this.bpmnModelerService.fitToViewport();
  }

  /**
   * Reset zoom to 100%
   */
  onResetZoom(): void {
    this.bpmnModelerService.resetZoom();
  }

  /**
   * Import BPMN file
   */
  async onImportFile(): Promise<void> {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.bpmn,.xml';
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (file) {
          const text = await file.text();
          await this.bpmnModelerService.importXml(text);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant(
              'bpmn.create.messages.import_success'
            ),
            detail: this.translateService.instant(
              'bpmn.create.messages.import_success_detail'
            ),
          });
          this.hasUnsavedChanges.set(true);
          this.cdr.markForCheck();
        }
      };
      input.click();
    } catch (error) {
      console.error('Failed to import BPMN file:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.create.messages.import_failed'
        ),
        detail: this.translateService.instant(
          'bpmn.create.messages.import_error_generic'
        ),
      });
    }
  }

  /**
   * Export BPMN as file
   */
  async exportBpmnAsFile(): Promise<void> {
    try {
      const result = await this.bpmnModelerService.exportXml();

      if (result.error) {
        console.error('Export failed:', result.error);
        return;
      }

      const processId = 'bpmn-process'; // Default name since we get process info from properties panel
      const blob = new Blob([result.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${processId}.bpmn`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export BPMN file:', error);
    }
  }

  /**
   * Export BPMN as image
   */
  async exportBpmnAsImage(): Promise<void> {
    try {
      const result = await this.bpmnModelerService.exportSvg();

      if (result.error) {
        console.error('SVG export failed:', result.error);
        return;
      }

      const processId = 'bpmn-process'; // Default name since we get process info from properties panel
      const blob = new Blob([result.svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${processId}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export BPMN image:', error);
    }
  }

  /**
   * Handle Export File button click
   */
  onExportFile(): void {
    this.exportBpmnAsFile();
  }

  /**
   * Handle Export Image button click
   */
  onExportImage(): void {
    this.exportBpmnAsImage();
  }

  ngOnDestroy(): void {
    this.bpmnModelerService.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.processForm?.dirty || this.hasUnsavedChanges());
  }

  /**
   * Handle Cancel button click
   */
  onCancel(): void {
    if (this.hasUnsavedChanges()) {
      // TODO: Show confirmation dialog if there are unsaved changes
      const confirmed = confirm(
        this.translateService.instant(
          'bpmn.create.unsaved_changes_confirmation'
        )
      );
      if (!confirmed) {
        return;
      }
    }
    this.router.navigate(['/configuration/master-data/bpmn']);
  }

  /**
   * Handle Save button click
   */
  async onSave(): Promise<void> {
    // Validate form
    if (this.processForm.invalid) {
      this.processForm.markAllAsTouched();
      return;
    }

    // Check for linting errors before saving
    const lintResults = this.bpmnLintService.getCurrentLintResults();

    // Check if there are any errors
    if (lintResults.hasErrors) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'bpmn.create.messages.validation_failed'
        ),
        detail: this.translateService.instant(
          'bpmn.create.messages.linting_errors_present'
        ),
      });
      return;
    }

    if (this.isSaveDisabled()) {
      return;
    }

    this.loading.set(true);

    try {
      // Export BPMN XML
      const xmlResult = await this.bpmnModelerService.exportXml();
      if (xmlResult.error) {
        throw new Error(xmlResult.error);
      }

      // Get form values and normalize Vietnamese text before submission
      const formValues = this.processForm.value;

      // Prepare payload for API call
      const payload: ProcessDefineDTO = {
        processName: this.textNormalizer.normalizeToNFC(formValues.processName || ''),
        processDefineId: this.textNormalizer.normalizeToNFC(formValues.processDefineId || ''),
        xmlString: xmlResult.xml,
      };

      // Make API call to create process definition
      this.processDefineService
        .create4(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.processForm.markAsPristine();
            this.hasUnsavedChanges.set(false);

            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant(
                'bpmn.create.messages.save_success'
              ),
              detail: this.translateService.instant(
                'bpmn.create.messages.save_success_detail'
              ),
            });

            // Navigate back to list after showing the toast message
            setTimeout(() => {
              this.router.navigate(['/configuration/master-data/bpmn']);
            }, 1500);
          },
          error: (error: any) => {
            console.error('Error creating BPMN process:', error);
            this.loading.set(false);
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant(
                'bpmn.create.messages.save_failed'
              ),
              detail:
                error?.error?.message ||
                error?.message ||
                'Unknown error occurred',
            });
          },
        });
    } catch (error) {
      console.error('Error preparing BPMN process:', error);
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant(
          'bpmn.create.messages.save_failed'
        ),
        detail: String(error),
      });
    }
  }
}
